import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { toLocalDateStr } from '../utils/dateUtils';

/**
 * Vérifie si la plage de dates traverse un week-end (samedi ou dimanche)
 * @param {string} startDate - Date de début (YYYY-MM-DD)
 * @param {string} endDate - Date de fin (YYYY-MM-DD)
 * @returns {boolean} true si un samedi ou dimanche est inclus dans la plage
 */
export const hasWeekendBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Si dates invalides
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

  // Parcourir chaque jour
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // 0 = dimanche, 6 = samedi
      return true;
    }
    current.setDate(current.getDate() + 1);
  }

  return false;
};

/**
 * Hook UNIFIÉ pour la gestion des données de prestation multi-jours
 *
 * RESPONSABILITÉS :
 * - Gestion des jours de la prestation (sessionDays)
 * - Affectation des participants par jour (dayParticipants)
 * - Flag "sur place" par participant/jour (participantOnSite)
 *
 * NOTE : Les heures sont gérées via les feuilles journalières (DailyWorksheet)
 * et non plus dans ce hook.
 */
const useMultiDayData = (formData, editingSession, token, showInfo, showError, setFormData) => {
  // Protection : Hook ID unique
  const hookId = useRef(null);
  if (!hookId.current) {
    hookId.current = Math.random().toString(36).substr(2, 9);
  }

  // Protection : Refs pour éviter les rechargements
  const dataLoadedRef = useRef(false);
  const stablePropsRef = useRef({});
  const previousDaysRef = useRef([]);

  // Stabilisation : Mémoriser les props critiques
  const stableProps = useMemo(() => {
    const newProps = {
      startDate: formData?.start_date || '',
      endDate: formData?.end_date || '',
      includeWeekends: formData?.include_weekends || false,
      sessionId: editingSession?.id || null,
      token: token || ''
    };

    const changed = JSON.stringify(newProps) !== JSON.stringify(stablePropsRef.current);
    if (changed) {
      stablePropsRef.current = newProps;
    }

    return newProps;
  }, [formData?.start_date, formData?.end_date, formData?.include_weekends, editingSession?.id, token]);

  // États : participants par jour, flag "sur place", chef d'équipe par jour
  const [sessionDays, setSessionDays] = useState([]);
  const [dayParticipants, setDayParticipants] = useState({});
  const [participantOnSite, setParticipantOnSite] = useState({});
  const [dayTeamLeaders, setDayTeamLeaders] = useState({}); // Override chef d'équipe par jour
  const [activeDayTab, setActiveDayTab] = useState(0);

  // Calcul des jours ouvrés entre start_date et end_date
  // NOTE: En mode édition, les jours chargés depuis la BDD (sessionDays) sont prioritaires
  const calculatedDays = useMemo(() => {
    logger.debug('🔍 [UNIFIED] Calcul jours:', {
      startDate: stableProps.startDate,
      endDate: stableProps.endDate
    });

    if (!stableProps.startDate || !stableProps.endDate) {
      return [];
    }

    const start = new Date(stableProps.startDate);
    const end = new Date(stableProps.endDate);

    // Calculer les jours ouvrés
    const days = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      // Inclure les jours selon l'option include_weekends
      const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      if (isWeekday || (isWeekend && stableProps.includeWeekends)) {
        const dateStr = toLocalDateStr(currentDate);
        const dayNumber = days.length + 1;

        const dayLabel = days.length === 0 && start.getTime() === end.getTime()
          ? 'Jour unique'
          : `Jour ${dayNumber} - ${currentDate.toLocaleDateString('fr-FR', {
              weekday: 'short',
              day: '2-digit',
              month: '2-digit'
            })}`;

        days.push({
          date: dateStr,
          label: dayLabel,
          dayNumber,
          isWeekend: isWeekend
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  }, [stableProps.startDate, stableProps.endDate, stableProps.includeWeekends]);

  // sessionInfo : Utilise les jours chargés depuis BDD si disponibles, sinon calcule
  const sessionInfo = useMemo(() => {
    // En mode édition avec données chargées, utiliser sessionDays (jours réels depuis BDD)
    if (stableProps.sessionId && sessionDays.length > 0 && dataLoadedRef.current) {
      logger.debug('✅ [UNIFIED] Utilisation des jours depuis BDD:', sessionDays.length);
      return {
        days: sessionDays,
        totalDays: sessionDays.length,
        isUnifiedMode: true,
        fromDatabase: true
      };
    }

    // Sinon utiliser les jours calculés
    logger.debug('✅ [UNIFIED] Session calculée:', {
      totalDays: calculatedDays.length,
      days: calculatedDays.map(d => d.date)
    });

    return {
      days: calculatedDays,
      totalDays: calculatedDays.length,
      isUnifiedMode: true,
      fromDatabase: false
    };
  }, [stableProps.sessionId, sessionDays, calculatedDays]);

  // Fonction pour récupérer les participants du jour précédent
  const getPreviousDayParticipants = useCallback((currentDate, allDays, participantsData) => {
    const currentIndex = allDays.findIndex(day => day.date === currentDate);

    if (currentIndex <= 0) {
      // Premier jour : utiliser formData si disponible
      return formData.participant_ids || [];
    }

    // Chercher le jour précédent qui a des participants
    for (let i = currentIndex - 1; i >= 0; i--) {
      const previousDay = allDays[i];
      const previousParticipants = participantsData[previousDay.date] || [];

      if (previousParticipants.length > 0) {
        return previousParticipants;
      }
    }

    // Fallback vers formData
    return formData.participant_ids || [];
  }, [formData.participant_ids]);

  // SYNCHRONISATION : Gestion des changements de jours
  // IMPORTANT: Ne pas synchroniser si les données viennent de la BDD (mode édition)
  useEffect(() => {
    if (sessionInfo.days.length === 0) {
      return;
    }

    // Si les jours viennent de la BDD, ne pas synchroniser (évite de recréer les jours supprimés)
    if (sessionInfo.fromDatabase) {
      logger.debug('⏭️ [UNIFIED] Jours depuis BDD - pas de synchronisation automatique');
      return;
    }

    logger.debug('🔄 [UNIFIED] === SYNCHRONISATION ===');
    logger.debug('📅 [UNIFIED] Jours actuels:', sessionInfo.days.map(d => d.date));

    // Protection contre l'écrasement des données existantes
    if (Object.values(dayParticipants).some(participants => participants.length > 0)) {
      logger.debug('⚠️ Des participants existent déjà - ARRÊT SYNCHRONISATION');
      return;
    }

    // Comparer avec les jours précédents
    const currentDates = sessionInfo.days.map(day => day.date);
    const previousDates = previousDaysRef.current.map(day => day.date);

    const newDates = currentDates.filter(date => !previousDates.includes(date));
    const removedDates = previousDates.filter(date => !currentDates.includes(date));

    // Traitement seulement si pas de chargement serveur en cours
    if (!dataLoadedRef.current) {
      // Nettoyage des jours supprimés
      if (removedDates.length > 0) {
        setDayParticipants(prev => {
          const cleaned = { ...prev };
          removedDates.forEach(date => delete cleaned[date]);
          return cleaned;
        });

        setParticipantOnSite(prev => {
          const cleaned = { ...prev };
          removedDates.forEach(date => delete cleaned[date]);
          return cleaned;
        });
      }

      // Ajout des nouveaux jours
      if (newDates.length > 0) {
        const existingParticipantsSnapshot = { ...dayParticipants };

        setDayParticipants(prev => {
          const updated = { ...prev };

          newDates.forEach(newDate => {
            const participants = getPreviousDayParticipants(
              newDate,
              sessionInfo.days,
              existingParticipantsSnapshot
            );
            updated[newDate] = [...participants];
          });

          return updated;
        });

        if (showInfo) {
          showInfo(`${newDates.length} nouveau(x) jour(s) ajouté(s)`, {
            title: '🆕 Synchronisation',
            details: 'Participants du jour précédent copiés automatiquement',
            duration: 3000
          });
        }
      }

      // Synchronisation sessionDays
      const newSessionDays = sessionInfo.days.map(day => ({
        date: day.date,
        label: day.label,
        dayNumber: day.dayNumber
      }));

      setSessionDays(newSessionDays);

      // Ajustement activeDayTab
      if (activeDayTab >= sessionInfo.days.length) {
        setActiveDayTab(Math.max(0, sessionInfo.days.length - 1));
      }
    }

    // Mettre à jour la référence
    previousDaysRef.current = [...sessionInfo.days];

  }, [
    sessionInfo.days,
    activeDayTab,
    showInfo,
    getPreviousDayParticipants,
    dayParticipants
  ]);

  // CHARGEMENT : Charger les données d'une session existante
  const loadSessionData = useCallback(async (sessionId) => {
    if (!sessionId || !token || dataLoadedRef.current) {
      return false;
    }

    try {
      logger.debug('📂 [UNIFIED] Chargement session:', sessionId);

      dataLoadedRef.current = true;

      // Charger les données multi-jours
      const daysResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS(sessionId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (daysResponse.ok) {
        const daysData = await daysResponse.json();

        if (daysData.success && daysData.data.length > 0) {
          logger.debug('📅 [UNIFIED] Données multi-jours trouvées:', daysData.data.length);

          // Transformer les données
          const transformedSessionDays = daysData.data.map((day) => ({
            id: day.id,
            date: day.day_date,
            dayNumber: day.day_number,
            label: day.day_number === 1 && daysData.data.length === 1
              ? 'Jour unique'
              : `Jour ${day.day_number} - ${new Date(day.day_date).toLocaleDateString('fr-FR', {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit'
                })}`,
            participants: day.participants || []
          }));

          const transformedDayParticipants = {};
          const transformedOnSite = {};
          const transformedDayTeamLeaders = {};

          daysData.data.forEach(day => {
            const dayDate = day.day_date;
            const participants = day.participants || [];

            transformedDayParticipants[dayDate] = participants.map(p => p.user_id);
            transformedOnSite[dayDate] = {};

            participants.forEach(participant => {
              transformedOnSite[dayDate][participant.user_id] = participant.is_on_site || false;
            });

            // Charger l'override chef d'équipe si présent
            if (day.team_leader_id_override) {
              transformedDayTeamLeaders[dayDate] = day.team_leader_id_override;
            }
          });

          setSessionDays(transformedSessionDays);
          setDayParticipants(transformedDayParticipants);
          setParticipantOnSite(transformedOnSite);
          setDayTeamLeaders(transformedDayTeamLeaders);

          previousDaysRef.current = [...sessionInfo.days];

          logger.debug('✅ [UNIFIED] Chargement terminé');
          return true;
        }
      }

      dataLoadedRef.current = false;
      return false;

    } catch (err) {
      logger.error('❌ [UNIFIED] Erreur chargement:', err);
      if (showError) {
        showError('Erreur chargement session', {
          title: '📂 Problème de chargement',
          message: 'Impossible de charger les données de la session'
        });
      }
      dataLoadedRef.current = false;
      return false;
    }
  }, [token, showError, sessionInfo.days]);

  // Gestion du flag "sur place" par participant
  const getParticipantOnSite = useCallback((userId) => {
    const currentDay = sessionInfo.days[activeDayTab];
    if (!currentDay) return false;

    return participantOnSite[currentDay.date]?.[userId] || false;
  }, [sessionInfo.days, activeDayTab, participantOnSite]);

  const setParticipantOnSiteFlag = useCallback((userId, isOnSite) => {
    const currentDay = sessionInfo.days[activeDayTab];
    if (!currentDay) return;

    setParticipantOnSite(prev => {
      const updated = { ...prev };
      if (!updated[currentDay.date]) {
        updated[currentDay.date] = {};
      }
      updated[currentDay.date][userId] = isOnSite;
      return updated;
    });
  }, [sessionInfo.days, activeDayTab]);

  // Reset pour nouvelle session
  const resetSessionData = useCallback(() => {
    setSessionDays([]);
    setDayParticipants({});
    setParticipantOnSite({});
    setDayTeamLeaders({});
    setActiveDayTab(0);
    dataLoadedRef.current = false;
    previousDaysRef.current = [];
  }, []);

  // API publique
  return {
    // Données calculées
    multiDayInfo: sessionInfo, // Alias pour compatibilité
    sessionInfo,

    // États
    sessionDays,
    setSessionDays,
    dayParticipants,
    setDayParticipants,
    activeDayTab,
    setActiveDayTab,

    // Gestion "sur place"
    participantOnSite,
    setParticipantOnSite,
    getParticipantOnSite,
    setParticipantOnSiteFlag,

    // Gestion chef d'équipe par jour
    dayTeamLeaders,
    setDayTeamLeaders,

    // Fonctions
    loadMultiDayData: loadSessionData, // Alias pour compatibilité
    loadSessionData,
    resetMultiDayData: resetSessionData, // Alias pour compatibilité
    resetSessionData,

    // Fonctions utilitaires
    getPreviousDayParticipants
  };
};

export default useMultiDayData;
